// AppShell (docs/ARCHITECTURE.md §8): created once per page. Owns the single WebGLRenderer, the single
// requestAnimationFrame loop, viewport tracking, visual settings and the screen router, and hands every screen the
// page's presentation tuning. Later phases add the client Core, AudioService and AssetLoader here.

import { SRGBColorSpace, WebGLRenderer } from 'three'
import { InvariantViolation } from '@taxwar/net'
import type { Viewport } from '../render/viewport'
import { QUALITY_PROFILES, type VisualSettings } from '../render/visualSettings'
import type { TuningLink } from '../tuning/link'
import type { FrameUpdater, Screen, ScreenContext, ScreenFactory } from './screen'

/** Longest frame step handed to updaters (a backgrounded tab must not produce one huge step). */
const MAX_FRAME_DT_SEC = 0.1
const MS_PER_SEC = 1000
/** A collapsed host still gets a 1×1 drawing buffer: zero-sized render targets are invalid. */
const MIN_VIEWPORT_PX = 1

let shellCreated = false

interface ActiveScreen {
  readonly screen: Screen
  readonly controller: AbortController
}

export class AppShell {
  private readonly renderer: WebGLRenderer
  private readonly lifetime = new AbortController()
  private readonly hostObserver: ResizeObserver
  /** Registration order; removed slots are tombstoned (null) and compacted after the frame. */
  private readonly updaters: (FrameUpdater | null)[] = []
  private iterating = false
  private updatersDirty = false
  private active: ActiveScreen | null = null
  private settings: VisualSettings
  private viewport: Viewport
  /** 0 while no frame is scheduled: before the first, or after an updater threw (the loop stopped). */
  private rafHandle = 0
  private lastFrameMs: number | null = null
  private disposed = false

  constructor(
    private readonly host: HTMLElement,
    settings: VisualSettings,
    private readonly tuning: TuningLink,
  ) {
    if (shellCreated) throw new InvariantViolation('AppShell is created once per page; a second renderer or frame loop is forbidden')
    shellCreated = true
    this.settings = settings
    // No canvas MSAA: every frame ends in a full-screen OutputPass; the post targets carry the tier's MSAA.
    this.renderer = new WebGLRenderer({ antialias: false, powerPreference: 'high-performance' })
    this.renderer.outputColorSpace = SRGBColorSpace
    host.appendChild(this.renderer.domElement)
    this.viewport = this.measureViewport()
    this.applyViewport()
    this.hostObserver = new ResizeObserver(this.onResize)
    this.hostObserver.observe(host)
    this.watchPixelRatio()
    this.scheduleFrame()
  }

  /**
   * Leaves the current screen (aborting its signal, then disposing it) and enters a new one. A loop stopped by a
   * throwing updater restarts here: the app flow shows its crash report (and later setup) through show().
   */
  show<S extends Screen>(factory: ScreenFactory<S>): S {
    this.assertLive()
    this.leaveScreen()
    if (this.rafHandle === 0) this.scheduleFrame()
    const controller = new AbortController()
    const context: ScreenContext = {
      renderer: this.renderer,
      viewport: this.viewport,
      settings: this.settings,
      tuning: this.tuning,
      signal: controller.signal,
      addUpdater: (updater) => this.addUpdater(updater, controller.signal),
    }
    let screen: S
    try {
      screen = factory(context)
    } catch (error) {
      controller.abort()
      throw error
    }
    this.active = { screen, controller }
    return screen
  }

  setVisualSettings(settings: VisualSettings): void {
    this.assertLive()
    this.settings = settings
    this.viewport = this.measureViewport()
    this.applyViewport()
    this.active?.screen.applySettings(settings)
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    cancelAnimationFrame(this.rafHandle)
    this.hostObserver.disconnect()
    // Also detaches every updater's abort listener, so leaving the screen below cannot re-enter removeUpdater.
    this.lifetime.abort()
    this.leaveScreen()
    this.updaters.length = 0
    this.renderer.dispose()
    this.renderer.domElement.remove()
  }

  private scheduleFrame(): void {
    // A restarted loop begins with a zero step, like the first frame.
    this.lastFrameMs = null
    this.rafHandle = requestAnimationFrame(this.frame)
  }

  private readonly frame = (nowMs: number): void => {
    this.rafHandle = 0
    const dt = this.lastFrameMs === null ? 0 : Math.min(MAX_FRAME_DT_SEC, (nowMs - this.lastFrameMs) / MS_PER_SEC)
    this.lastFrameMs = nowMs
    this.iterating = true
    try {
      for (let i = 0; i < this.updaters.length; i++) {
        const updater = this.updaters[i]
        if (updater) updater(dt)
      }
    } finally {
      this.iterating = false
      if (this.updatersDirty) this.compactUpdaters()
    }
    // Scheduled last: an updater that throws stops the loop loudly instead of rethrowing every frame.
    if (!this.disposed) this.rafHandle = requestAnimationFrame(this.frame)
  }

  private readonly onResize = (): void => {
    this.viewport = this.measureViewport()
    this.applyViewport()
  }

  /** Moving between monitors changes devicePixelRatio without resizing the host; the query fires once per change. */
  private watchPixelRatio(): void {
    const query = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`)
    query.addEventListener('change', this.onPixelRatioChange, { once: true, signal: this.lifetime.signal })
  }

  private readonly onPixelRatioChange = (): void => {
    this.onResize()
    this.watchPixelRatio()
  }

  /** Runs `updater` every frame until `signal` aborts (or the shell is disposed). */
  private addUpdater(updater: FrameUpdater, signal: AbortSignal): void {
    this.assertLive()
    if (signal.aborted) throw new InvariantViolation('frame updater registered with an already-aborted signal')
    this.updaters.push(updater)
    signal.addEventListener('abort', () => this.removeUpdater(updater), { once: true, signal: this.lifetime.signal })
  }

  private removeUpdater(updater: FrameUpdater): void {
    const index = this.updaters.indexOf(updater)
    if (index < 0) throw new InvariantViolation('frame updater removed twice')
    this.updaters[index] = null
    this.updatersDirty = true
    if (!this.iterating) this.compactUpdaters()
  }

  private compactUpdaters(): void {
    let write = 0
    for (let read = 0; read < this.updaters.length; read++) {
      const updater = this.updaters[read]
      if (updater) this.updaters[write++] = updater
    }
    this.updaters.length = write
    this.updatersDirty = false
  }

  private leaveScreen(): void {
    const active = this.active
    if (!active) return
    this.active = null
    active.controller.abort()
    active.screen.dispose()
  }

  private measureViewport(): Viewport {
    return {
      width: Math.max(MIN_VIEWPORT_PX, this.host.clientWidth),
      height: Math.max(MIN_VIEWPORT_PX, this.host.clientHeight),
      pixelRatio: Math.min(window.devicePixelRatio, QUALITY_PROFILES[this.settings.quality].maxPixelRatio),
    }
  }

  private applyViewport(): void {
    this.renderer.setPixelRatio(this.viewport.pixelRatio)
    this.renderer.setSize(this.viewport.width, this.viewport.height, false)
    this.active?.screen.resize(this.viewport)
  }

  private assertLive(): void {
    if (this.disposed) throw new InvariantViolation('AppShell used after dispose')
  }
}
